import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, Table, TableHead, TableRow, TableCell, TableBody, TablePagination, Stack, FormControl, InputLabel, Select, MenuItem, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Snackbar, Alert, Tabs, Tab, TableContainer, IconButton, Tooltip, Typography } from '@mui/material';
import { Delete as DeleteIcon } from '@mui/icons-material';
import StatusChip from '../StatusChip';
import api from '../../services/api';
import { usePermissions } from '../PermissionsContext';
import { getEmployeeFullName } from '../../utils/names';
import { useToast } from '../ToastProvider';
import { toYyyyMmDd } from '../../utils/businessDays';

export default function PendingApprovals(props){
  const [items, setItems] = useState([]);
  const [statusFilter, setStatusFilter] = useState('PendingDM'); // default for DM stage
  const [roleTab, setRoleTab] = useState('DM'); // 'DM' | 'BOARD'
  const effectiveStatusDefault = roleTab==='BOARD' ? 'PendingBoard' : 'PendingDM';
  const [employeeFilter, setEmployeeFilter] = useState(''); // '' means ALL
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [rejectDlg, setRejectDlg] = useState({ open:false, id:null, comment:'' });
  const [deleteDlg, setDeleteDlg] = useState({ open:false, request:null });
  const [localToast, setLocalToast] = useState({ open:false, msg:'', sev:'success' });
  const toast = useToast();
  const { isSystemAdmin, roles } = usePermissions();
  const isBoardManager = roles?.includes('BoardManager');
  const isDepartmentManager = roles?.includes('DepartmentManager');

  const load = async () => {
    try {
      setItems([]);
      const params = {};
      if (props?.from) params.from = props.from;
      if (props?.to) params.to = props.to;
      if (props?.month) params.month = props.month;

      let arr = [];
      // Choose endpoint by role tab to avoid 403 for DM-only users
      if (roleTab === 'DM' && (isDepartmentManager || !isSystemAdmin)) {
        const dm = await api.get('/leave-requests/department', { params });
        arr = Array.isArray(dm) ? dm : [];
      } else if (roleTab === 'BOARD' && (isBoardManager || isSystemAdmin)) {
        // SysAdmin with ALL needs full dataset
        if (isSystemAdmin && statusFilter === 'ALL') {
          const all = await api.get('/leave-requests', { params });
          arr = Array.isArray(all) ? all : [];
        } else {
          const board = await api.get('/leave-requests/board', { params });
          arr = Array.isArray(board) ? board : [];
        }
      } else {
        // Fallback/global view (admins)
        const all = await api.get('/leave-requests', { params });
        arr = Array.isArray(all) ? all : [];
      }

      // Filter by department on client (still useful)
      const byDept = (props?.departmentId) ? arr.filter(r => (r.departmentId||r.DepartmentId||props?.employeesById?.[r.employeeId||r.EmployeeId]?.departmentId) === props.departmentId) : arr;
      setItems(byDept);
      if (props?.onSummary) {
        const pend = byDept.filter(r => (r.status||r.Status) === 'PendingDM' || (r.status||r.Status) === 'PendingBoard').length;
        const canc = byDept.filter(r => (r.status||r.Status) === 'CancelRequested').length;
        props.onSummary({ pending: pend, cancel: canc });
      }
    } catch (e) {
      const msg = e?.response?.status === 403 ? 'Нямате права за този изглед. Превключете таба или ограничете изгледа.' : (e?.response?.data || e.message);
      toast.error(msg);
    }
  };

  useEffect(() => { load(); }, []);
  // Reload when role tab changes (DM <-> BOARD) and normalize Status filter default
  useEffect(() => {
    setStatusFilter(roleTab==='BOARD' ? 'PendingBoard' : 'PendingDM');
    setPage(0);
    load();
  }, [roleTab]);

  // Reload when Status changes to reflect ALL behavior (esp. SysAdmin)
  useEffect(() => {
    load();
  }, [statusFilter]);


  // получаваме employeeIdFilter през props; '' означава ALL
  useEffect(()=>{
    setEmployeeFilter(props?.employeeIdFilter || '');
  }, [props?.employeeIdFilter]);


  const act = async (id, action, comment) => {
    try {
      if(action==='approve') await api.post(`/leave-requests/${id}/approve`);
      if(action==='reject') await api.post(`/leave-requests/${id}/reject`, comment||'');
      if(action==='dm-reject') await api.post(`/leave-requests/${id}/dm-reject`, comment||'');
      if(action==='approve-cancel') await api.post(`/leave-requests/${id}/approve-cancel`);
      if(action==='reject-cancel') await api.post(`/leave-requests/${id}/reject-cancel`, comment||'');
      // Success toast + refresh
      if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent('hrapp.toast', { detail:{ sev:'success', msg:'Операцията е успешна' } }));
      load();
      if (props?.onActionSuccess) props.onActionSuccess();
      // Fire a global event so other widgets (e.g., AttendanceMap) can refresh without full page reload
      if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent('hrapp.leave.changed'));
    } catch(e){
      const msg = e?.response?.data || e.message;
      if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent('hrapp.toast', { detail:{ sev:'error', msg } }));
    }
  };

  const handleDelete = async () => {
    if (!deleteDlg.request) return;
    try {
      await api.delete(`/leave-requests/${deleteDlg.request.id || deleteDlg.request.Id}`);
      if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent('hrapp.toast', { detail:{ sev:'success', msg:'Заявката е изтрита и балансът е възстановен' } }));
      setDeleteDlg({ open:false, request:null });
      load();
      if (props?.onActionSuccess) props.onActionSuccess();
      if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent('hrapp.leave.changed'));
    } catch(e){
      const msg = e?.response?.data || e.message;
      if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent('hrapp.toast', { detail:{ sev:'error', msg } }));
    }
  };

  const listFiltered = useMemo(() => {
    // 1) Scope by role tab (but for SysAdmin + ALL do not restrict by tab)
    const byTab = (isSystemAdmin && statusFilter === 'ALL')
      ? items
      : items.filter(r => {
        const st = (r.status||r.Status);
        if (roleTab === 'DM') return st === 'PendingDM' || st === 'Approved' || st === 'Rejected' || st === 'Cancelled';
        if (roleTab === 'BOARD') return st === 'PendingBoard' || st === 'CancelRequested' || st === 'Approved' || st === 'Rejected' || st === 'Cancelled';
        return true;
      });
    // 2) Status filter
    const byStatus = statusFilter==='ALL' ? byTab : byTab.filter(r => (r.status||r.Status) === statusFilter);
    // 3) Employee quick filter
    if (!employeeFilter) return byStatus;
    const q = employeeFilter.toLowerCase();
    return byStatus.filter(r => {
      const emp = props?.employeesById?.[r.employeeId||r.EmployeeId];
      const name = (r.employeeName || r.EmployeeName || getEmployeeFullName(emp) || '')
        .toString().toLowerCase();
      return name.includes(q);
    });
  }, [items, statusFilter, employeeFilter, props?.employeesById, roleTab, isSystemAdmin]);

  const start = page * rowsPerPage;
  const slice = listFiltered.slice(start, start + rowsPerPage);

  return (
    <Box>
      <Stack direction={{ xs:'column', sm:'row' }} spacing={2} alignItems="center" sx={{ mb:2 }}>
        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel id="status-filter-label">Status</InputLabel>
          <Select labelId="status-filter-label" label="Status" value={statusFilter} onChange={(e)=>{ setStatusFilter(e.target.value); setPage(0); }}>
            <MenuItem value="ALL">All</MenuItem>
            { (roleTab==='DM') && <MenuItem value="PendingDM">Pending (DM)</MenuItem> }
            { (roleTab==='BOARD') && <MenuItem value="PendingBoard">Pending (Board)</MenuItem> }
            { (roleTab==='BOARD') && <MenuItem value="CancelRequested">Cancel Requested</MenuItem> }
            <MenuItem value="Approved">Approved</MenuItem>
            <MenuItem value="Rejected">Rejected</MenuItem>
            <MenuItem value="Cancelled">Cancelled</MenuItem>
          </Select>
        </FormControl>
      </Stack>
        {/* Role tabs */}
        {(isBoardManager || isSystemAdmin) && (isDepartmentManager || isSystemAdmin) && (
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb:1 }}>
            <Tabs value={roleTab} onChange={(e, v)=> setRoleTab(v)} aria-label="Approval scope tabs">
              <Tab value="DM" label="Department" />
              <Tab value="BOARD" label="Board" />
            </Tabs>
          </Box>
        )}


      <TableContainer sx={{ overflowX:'auto' }}>
      <Table size="small" sx={{ minWidth: 800 }}>
        <TableHead>
          <TableRow>
            <TableCell>Employee</TableCell>
            <TableCell>Type</TableCell>
            <TableCell>Start</TableCell>
            <TableCell>End</TableCell>
            <TableCell>Business Days</TableCell>
            <TableCell>Reason</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Approved by</TableCell>
            <TableCell>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {listFiltered.length===0 && (
            <TableRow><TableCell colSpan={8} align="center">No items</TableCell></TableRow>
          )}
          {slice.length===0 && listFiltered.length>0 && (
            <TableRow><TableCell colSpan={8} align="center">Loading…</TableCell></TableRow>
          )}

          {slice.map(r => (
            <TableRow key={r.id||r.Id}>
              <TableCell>{ r.employeeName || r.EmployeeName || (()=>{ const emp = props?.employeesById?.[r.employeeId||r.EmployeeId]; const name = (emp?.fullName || `${emp?.person?.firstName || emp?.firstName || ''} ${emp?.person?.lastName || emp?.lastName || ''}`.trim()); return name || (r.employeeId||r.EmployeeId); })() }</TableCell>
              <TableCell>{r.leaveTypeName || r.LeaveTypeName || r.leaveTypeId || r.LeaveTypeId}</TableCell>
              <TableCell>{r.startDateLocalYmd || r.StartDateLocalYmd || toYyyyMmDd(new Date(r.startDate||r.StartDate))}</TableCell>
              <TableCell>{r.endDateLocalYmd || r.EndDateLocalYmd || toYyyyMmDd(new Date(r.endDate||r.EndDate))}</TableCell>
              <TableCell>{r.businessDays||r.BusinessDays}</TableCell>
              <TableCell>{r.reason||r.Reason}</TableCell>
              <TableCell><StatusChip status={r.status||r.Status} /></TableCell>
              <TableCell>{r.approvedByName || r.ApprovedByName || ''}</TableCell>
              <TableCell>
                <>
                  {(isDepartmentManager || isSystemAdmin) && ((r.status||r.Status)==='PendingDM') && (
                    <>
                      <Button size="small" onClick={()=>api.post(`/leave-requests/${r.id||r.Id}/dm-approve`).then(()=>load())}>Approve (DM)</Button>
                      <Button size="small" color="error" onClick={()=> setRejectDlg({ open:true, id: (r.id||r.Id), comment:'', kind:'dm-reject' }) }>Reject (DM)</Button>
                    </>
                  )}

                  {(isBoardManager || isSystemAdmin) && ((r.status||r.Status)==='PendingBoard') && (
                    <>
                      <Button size="small" onClick={()=>act(r.id||r.Id,'approve')}>Approve</Button>
                      <Button size="small" color="error" onClick={()=> setRejectDlg({ open:true, id: (r.id||r.Id), comment:'' }) }>Reject</Button>
                    </>
                  )}

                  {(isBoardManager || isSystemAdmin) && ((r.status||r.Status)==='CancelRequested') && (
                    <>
                      <Button size="small" onClick={()=>act(r.id||r.Id,'approve-cancel')}>Approve Cancel</Button>
                      <Button size="small" color="error" onClick={()=> setRejectDlg({ open:true, id: (r.id||r.Id), comment:'', kind:'reject-cancel' }) }>Reject Cancel</Button>
                    </>
                  )}

                  {/* Delete button for SystemAdmin */}
                  {isSystemAdmin && (
                    <Tooltip title="Изтриване (възстановява баланса)">
                      <IconButton size="small" color="error" onClick={() => setDeleteDlg({ open:true, request:r })}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}

                  { !isSystemAdmin && !((isDepartmentManager) && (r.status||r.Status)==='PendingDM') &&
                    !((isBoardManager) && (r.status||r.Status)==='PendingBoard') &&
                    !((isBoardManager) && (r.status||r.Status)==='CancelRequested') && (
                      <em>—</em>
                    )
                  }
                </>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={listFiltered.length}
        page={page}
        onPageChange={(e, newPage)=>setPage(newPage)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e)=>{ setRowsPerPage(parseInt(e.target.value,10)); setPage(0); }}
        rowsPerPageOptions={[5,10,20,50]}
      />

      <Dialog open={rejectDlg.open} onClose={()=>setRejectDlg({ open:false, id:null, comment:'', kind: undefined })}>
        <DialogTitle>Причина за отхвърляне (по желание)</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Коментар"
            type="text"
            fullWidth
            variant="standard"
            value={rejectDlg.comment}
            onChange={(e)=>setRejectDlg(d=>({ ...d, comment: e.target.value }))}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={()=>setRejectDlg({ open:false, id:null, comment:'', kind: undefined })}>Cancel</Button>
          <Button color="error" onClick={()=>{
            const id = rejectDlg.id; const c = rejectDlg.comment || '';
            const kind = rejectDlg.kind || 'reject';
            setRejectDlg({ open:false, id:null, comment:'', kind: undefined });
            act(id, kind, c);
          }}>Reject</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDlg.open} onClose={() => setDeleteDlg({ open:false, request:null })}>
        <DialogTitle>Потвърждение за изтриване</DialogTitle>
        <DialogContent>
          <Typography>
            Сигурни ли сте, че искате да изтриете тази заявка за отпуск?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt:1 }}>
            Балансът на служителя ще бъде възстановен автоматично.
          </Typography>
          {deleteDlg.request && (
            <Box sx={{ mt:2, p:2, bgcolor:'grey.100', borderRadius:1 }}>
              <Typography variant="body2">
                <strong>{deleteDlg.request.employeeName || deleteDlg.request.EmployeeName}</strong><br />
                {deleteDlg.request.leaveTypeName || deleteDlg.request.LeaveTypeName}<br />
                {toYyyyMmDd(new Date(deleteDlg.request.startDate || deleteDlg.request.StartDate))} - {toYyyyMmDd(new Date(deleteDlg.request.endDate || deleteDlg.request.EndDate))}<br />
                {deleteDlg.request.businessDays || deleteDlg.request.BusinessDays} дни • <StatusChip status={deleteDlg.request.status || deleteDlg.request.Status} />
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDlg({ open:false, request:null })}>Отказ</Button>
          <Button color="error" variant="contained" onClick={handleDelete}>Изтриване</Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
}

